import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { LuArrowLeft, LuChevronDown, LuMail } from 'react-icons/lu';
import Page from '../../components/ui/Page/Page';
import PageHeader from '../../components/ui/PageHeader/PageHeader';
import Card from '../../components/ui/Card/Card';
import Button from '../../components/ui/Button/Button';
import FAQ_TOPICS from '../../content/faq.js';
import { SUPPORT_EMAIL, SUPPORT_MAILTO } from '../../config/support.js';
import { isNative } from '../../platform/platform.js';
import { openExternal } from '../../platform/navigation.js';
import './FaqPage.less';

/**
 * FAQ — `/support/faq`, reached from "Help centre" on the Support page.
 *
 * Content lives in src/content/faq.js; this page only renders it.
 * Each question is a disclosure button (aria-expanded / aria-controls)
 * controlling a labelled region, so screen readers announce state and
 * the answer is reachable by keyboard. Answers are independent — opening
 * one does not close another.
 */
function FaqItem({ item }) {
  const [open, setOpen] = useState(false);
  const buttonId = `gc-faq-q-${item.id}`;
  const panelId = `gc-faq-a-${item.id}`;
  return (
    <div className={`gc-faq-item${open ? ' is-open' : ''}`}>
      <h3 className="gc-faq-question">
        <button
          type="button"
          id={buttonId}
          className="gc-faq-toggle"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((v) => !v)}
        >
          <span>{item.question}</span>
          <LuChevronDown className="gc-faq-chevron" aria-hidden="true" />
        </button>
      </h3>
      <div
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
        className="gc-faq-answer"
        hidden={!open}
      >
        {item.answer.map((p, i) => <p key={i}>{p}</p>)}
      </div>
    </div>
  );
}

function FaqPage() {
  // Same native/web split as SupportPage: web honours the anchor
  // default; native hands mailto: to the OS via openExternal.
  const onEmailClick = useCallback((e) => {
    if (!isNative()) return;
    e.preventDefault();
    openExternal(SUPPORT_MAILTO).catch(() => {});
  }, []);

  return (
    <Page>
      <PageHeader
        title="Frequently asked questions"
        subtitle="Quick answers about charging, payments and your account."
        action={
          <Button as={Link} to="/support" variant="ghost" leadingIcon={<LuArrowLeft />}>
            Back
          </Button>
        }
      />

      {FAQ_TOPICS.map((topic) => (
        <section key={topic.id} className="gc-faq-topic" aria-labelledby={`gc-faq-t-${topic.id}`}>
          <h2 id={`gc-faq-t-${topic.id}`} className="gc-faq-topic-title">{topic.title}</h2>
          <Card className="gc-faq-card">
            {topic.items.map((item) => <FaqItem key={item.id} item={item} />)}
          </Card>
        </section>
      ))}

      <Card tone="subtle" className="gc-faq-more">
        <h2 className="gc-faq-topic-title">Still need help?</h2>
        <p>
          <a href={SUPPORT_MAILTO} onClick={onEmailClick} className="gc-faq-mail">
            <LuMail aria-hidden="true" />
            <span>Email support</span>
          </a>
          {' '}at {SUPPORT_EMAIL}.
        </p>
      </Card>
    </Page>
  );
}

export default FaqPage;
